// src/utils/courseCatalog.js
//
// THE single source of truth for "which tracks exist and which
// courses belong to each one." Four files used to keep their own
// copy of this knowledge (AiCourseAssistancePage, PricingPage,
// SignUp, CourseHubDashboard) and they drifted apart — the advisor
// promised courses that didn't exist in the Course Hub, and the Hub
// had categories the advisor knew nothing about. Now all four import
// from here, so a track a student picks on the advisor is exactly
// the set of courses they see in the Hub.
//
// TO ADD A COURSE TO A TRACK: add one line to that track's `courses`
// below. The `label` must match the course's `label` in
// CourseHubDashboard.jsx's ALL_COURSES exactly (the Hub warns in the
// browser console, in development, if anything doesn't line up).
//
// TO ADD A NEW TRACK: add one entry to TRACKS. The advisor, pricing
// page, signup form and Hub chips all pick it up automatically.
//
// A course may belong to MORE than one track (C++ is in both Backend
// and Embedded & IoT). A student can see a course if ANY of their
// tracks includes it.
//
// `level` and `weeks` are ESTIMATES used only by the advisor's
// roadmap display — adjust them to the real length of each course.
// ============================================================

export const LEVELS = ["Foundation", "Core", "Advanced", "Mastery"];

export const TRACKS = [
    {
        id: "frontend", title: "Frontend Developer", short: "Frontend",
        icon: "⬡", tagline: "Craft stunning user interfaces",
        courses: [
            { label: "HTML5",           level: "Foundation", weeks: 3 },
            { label: "CSS",             level: "Foundation", weeks: 4 },
            { label: "JavaScript",      level: "Core",       weeks: 6 },
            { label: "React Course",    level: "Core",       weeks: 8 },
            { label: "TypeScript",      level: "Advanced",   weeks: 4 },
            { label: "UI/UX Dashboard", level: "Mastery",    weeks: 3 },
        ],
    },
    {
        id: "backend", title: "Backend Developer", short: "Backend",
        icon: "⬢", tagline: "Build powerful server systems",
        courses: [
            { label: "Github",                  level: "Foundation", weeks: 2 },
            { label: "Linux",                   level: "Foundation", weeks: 3 },
            { label: "C++",                     level: "Foundation", weeks: 6 },
            { label: "Java",                    level: "Foundation", weeks: 6 },
            { label: "Clean Code",              level: "Core",       weeks: 3 },
            { label: "Express.js",              level: "Core",       weeks: 5 },
            { label: "Spring Boot",             level: "Core",       weeks: 6 },
            { label: "Software-Testing",        level: "Advanced",   weeks: 4 },
            { label: "Docker",                  level: "Advanced",   weeks: 4 },
            { label: "Design Patterns",         level: "Advanced",   weeks: 4 },
            { label: "SpringBoot Architecture", level: "Mastery",    weeks: 5 },
        ],
    },
    {
        // Composite track: no courses of its own, it is simply the
        // union of the tracks it `includes` (base tracks only — don't
        // nest composites inside composites).
        id: "fullstack", title: "Fullstack Developer", short: "Fullstack",
        icon: "⬟", tagline: "Master the complete stack",
        includes: ["frontend", "backend"],
    },
    {
        id: "database", title: "Database Administrator", short: "Database",
        icon: "⬠", tagline: "Govern the world's data",
        courses: [
            { label: "Python",  level: "Foundation", weeks: 6 },
            { label: "MySQL",   level: "Core",       weeks: 5 },
            { label: "MongoDB", level: "Advanced",   weeks: 4 },
        ],
    },
    {
        id: "iot", title: "Embedded & IoT Engineer", short: "Embedded & IoT",
        icon: "⬣", tagline: "Program the physical world",
        courses: [
            { label: "C++",           level: "Foundation", weeks: 6 },
            { label: "EmbeddedC-IoT", level: "Core",       weeks: 8 },
        ],
    },
    {
        id: "systemdesign", title: "System Design Architect", short: "System Design",
        icon: "❖", tagline: "Design systems that scale",
        courses: [
            { label: "System Design", level: "Mastery", weeks: 4 },
        ],
    },
    {
        id: "cloud", title: "Cloud Engineer", short: "Cloud",
        icon: "◇", tagline: "Build and run it on the cloud",
        courses: [
            { label: "Google Cloud", level: "Core", weeks: 5 },
        ],
    },
    {
        id: "ai", title: "AI & LLM Engineer", short: "AI & LLM",
        icon: "✦", tagline: "Build with large language models",
        courses: [
            { label: "AI & LLM", level: "Core", weeks: 6 },
        ],
    },
];

// Courses every logged-in student can see regardless of track.
// Empty on purpose: every course now belongs to a track. If you ever
// want a universal course again, put its label here.
export const SHARED_COURSE_LABELS = [];

// ------------------------------------------------------------
//  Derived lookups (computed once; nothing below needs editing
//  when you add courses or tracks)
// ------------------------------------------------------------

const trackById = {};
TRACKS.forEach((t) => { trackById[t.id] = t; });

const isBaseTrack = (track) => Array.isArray(track.courses);

const sortByLevel = (courses) =>
    [...courses].sort((a, b) => LEVELS.indexOf(a.level) - LEVELS.indexOf(b.level));

export const getTrackById = (id) => trackById[id] || null;

export const TRACK_IDS = TRACKS.map((t) => t.id);

// Tracks that own courses (everything except composites like fullstack)
export const BASE_TRACK_IDS = TRACKS.filter(isBaseTrack).map((t) => t.id);

/**
 * Ordered course list for a track, ready for the advisor roadmap.
 * Composite tracks return the de-duplicated union of what they include.
 */
export const getTrackCourses = (trackId) => {
    const track = trackById[trackId];
    if (!track) return [];
    if (isBaseTrack(track)) return sortByLevel(track.courses);

    const seen = new Set();
    const merged = [];
    (track.includes || []).forEach((includedId) => {
        const included = trackById[includedId];
        if (!included || !isBaseTrack(included)) return;
        included.courses.forEach((course) => {
            if (!seen.has(course.label)) {
                seen.add(course.label);
                merged.push(course);
            }
        });
    });
    return sortByLevel(merged);
};

// course label -> ids of the BASE tracks that include it
const courseTrackMap = {};
TRACKS.filter(isBaseTrack).forEach((track) => {
    track.courses.forEach((course) => {
        if (!courseTrackMap[course.label]) courseTrackMap[course.label] = [];
        courseTrackMap[course.label].push(track.id);
    });
});

export const getTrackIdsForCourse = (label) => courseTrackMap[label] || [];

export const getTrackLabelsForCourse = (label) =>
    getTrackIdsForCourse(label).map((id) => trackById[id].short);

// Every distinct course that belongs to at least one track
export const ALL_COURSE_LABELS = Object.keys(courseTrackMap);
